// Thin wrapper around window.fbq (Meta Pixel) and window.gtag (Google Analytics).
// All functions silently no-op when the respective global is missing, so callers
// never need to guard — the storefront injects fbq/gtag only when the vendor has
// the corresponding module enabled + a valid ID saved.
//
// Event names follow Meta standard events + GA4 recommended events.

declare global {
  interface Window {
    fbq?: (...args: any[]) => void
    gtag?: (...args: any[]) => void
  }
}

function callFbq(event: string, data?: Record<string, any>, eventId?: string) {
  if (typeof window === 'undefined' || typeof window.fbq !== 'function') return
  try {
    if (eventId) window.fbq('track', event, data, { eventID: eventId })
    else window.fbq('track', event, data)
  } catch { /* ignore */ }
}

function callGtag(event: string, data?: Record<string, any>) {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return
  try { window.gtag('event', event, data) } catch { /* ignore */ }
}

function productValue(variant: any): number {
  if (!variant) return 0
  if (variant.priceSale && variant.priceSale > 0 && variant.priceSale < variant.price) return variant.priceSale
  return variant.price || 0
}

export function trackViewContent(product: any) {
  if (!product) return
  const variant = product.variant || product.variants?.[0]
  const value = productValue(variant)
  callFbq('ViewContent', {
    content_ids: [product.id],
    content_name: product.name,
    content_type: 'product',
    value,
    currency: 'MNT',
  })
  callGtag('view_item', {
    currency: 'MNT',
    value,
    items: [{ item_id: product.id, item_name: product.name, price: value }],
  })
}

export function trackAddToCart({ product, variant, quantity = 1 }: { product: any, variant: any, quantity?: number }) {
  if (!product) return
  const value = productValue(variant) * quantity
  callFbq('AddToCart', {
    content_ids: [product.id],
    content_name: product.name,
    content_type: 'product',
    value,
    currency: 'MNT',
  })
  callGtag('add_to_cart', {
    currency: 'MNT',
    value,
    items: [{ item_id: product.id, item_name: product.name, price: productValue(variant), quantity }],
  })
}

export function trackInitiateCheckout({ products, totalValue }: { products: any[], totalValue: number }) {
  const contentIds = (products || []).map(p => p.id).filter(Boolean)
  callFbq('InitiateCheckout', {
    content_ids: contentIds,
    num_items: products?.length || 0,
    value: totalValue,
    currency: 'MNT',
  })
  callGtag('begin_checkout', {
    currency: 'MNT',
    value: totalValue,
    items: (products || []).map(p => ({
      item_id: p.id,
      item_name: p.name,
      price: productValue(p.variant),
      quantity: p.quantity || 1,
    })),
  })
}

export function trackAddPaymentInfo({ products, totalValue }: { products: any[], totalValue: number }) {
  const contentIds = (products || []).map(p => p.id).filter(Boolean)
  callFbq('AddPaymentInfo', {
    content_ids: contentIds,
    num_items: products?.length || 0,
    value: totalValue,
    currency: 'MNT',
  })
  callGtag('add_payment_info', {
    currency: 'MNT',
    value: totalValue,
    items: (products || []).map(p => ({
      item_id: p.id,
      item_name: p.name,
      price: productValue(p.variant),
      quantity: p.quantity || 1,
    })),
  })
}

function readCookie(name: string) {
  if (typeof document === 'undefined') return null
  const match = document.cookie.split('; ').find(c => c.startsWith(`${name}=`))
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null
}

export function getMetaTrackingContext() {
  if (typeof window === 'undefined') return null
  return { fbp: readCookie('_fbp'), fbc: readCookie('_fbc'), eventSourceUrl: window.location.href }
}

export function trackPurchase(order: any) {
  if (!order?.id) return
  const items = (order.products || []).filter((p: any) => !p.isRewardItem).map((p: any) => {
    const product = p.product || p
    return { id: product.id || p.productId, name: product.name, price: productValue(p.variant), quantity: p.quantity || 1 }
  })
  const value = (order.amount || 0) - (order.totalRefunded || 0)
  callFbq('Purchase', {
    content_ids: items.map((i: any) => i.id).filter(Boolean),
    content_type: 'product',
    num_items: items.length,
    value,
    currency: 'MNT',
    order_id: order.customId,
  }, `purchase_${order.id}`)
  callGtag('purchase', {
    transaction_id: order.customId || order.id,
    currency: 'MNT',
    value,
    items: items.map((i: any) => ({ item_id: i.id, item_name: i.name, price: i.price, quantity: i.quantity })),
  })
}
